import React from 'react';
import clsx from 'clsx';
import {faFolder} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Skeleton, Typography} from '@mui/material';
import {useAssetImage} from '@netx/core-asset-next/hooks/useAssetImage';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {newlineStringToElements} from '@netx/core-next/utils/react';
import {type ICategory} from '../../schema';
import {
	CATEGORY_CARD_NAME,
	type CategoryCardClasses,
	categoryCardClasses,
	getCategoryCardUtilityClass,
} from './categoryCardClasses';

type CategoryCardOwnerState = WithClasses<
	CategoryCardClasses,
	{
		hasDescription?: boolean;
		hasImage?: boolean;
	}
>;

const useUtilityClasses = (state: CategoryCardOwnerState) => {
	const {classes, hasDescription, hasImage} = state;
	const slots = {
		root: ['root', hasDescription && 'hasDescription', hasImage && 'hasImage'],
		actionBox: ['actionBox'],
		imageContainer: ['imageContainer'],
		description: ['description'],
		titleBox: ['titleBox'],
		svgIcon: ['svgIcon'],
	};
	return composeClasses(slots, getCategoryCardUtilityClass, classes);
};

const CategoryCardRoot = styled('a', {
	name: CATEGORY_CARD_NAME,
	slot: 'Root',
})(({theme, ownerState}) => ({
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	height: '100%',
	width: '100%',
	textDecoration: 'none',
	position: 'relative',
	overflow: 'hidden',
	...(ownerState.hasDescription
		? {
				'&:hover': {
					[`& .${categoryCardClasses.description}`]: {
						opacity: 1,
					},
				},
		  }
		: {
				[`& .${categoryCardClasses.actionBox}`]: {
					'&::after': {
						content: '""',
						display: 'block',
						width: '100%',
						height: '100%',
						position: 'absolute',
						backgroundColor: theme.palette.primary.main,
						opacity: 0,
						pointerEvents: 'none',
						transition: 'opacity 0.2s ease-in-out',
					},
				},
				'&:hover': {
					[`& .${categoryCardClasses.actionBox}`]: {
						'&::after': {
							opacity: ownerState.hasImage ? 0.25 : 0.08,
						},
					},
				},
		  }),
}));

const CategoryCardActionBox = styled('div', {
	name: CATEGORY_CARD_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	position: 'relative',
	height: '85%',
	width: '100%',
	backgroundColor: theme.palette.custom.background.accent,
}));

const CategoryImageContainer = styled('img', {
	name: CATEGORY_CARD_NAME,
	slot: 'Root',
})(({theme}) => ({
	// display: 'flex',
	objectFit: 'cover',
	objectPosition: `0 0`,
	width: '100%',
	height: '100%',
}));

const CategoryDescription = styled(Typography, {
	name: CATEGORY_CARD_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	opacity: 0,
	color: theme.palette.primary.contrastText,
	alignItems: 'center',
	justifyContent: 'center',
	padding: theme.spacing(1),
	textAlign: 'center',
	backgroundColor: theme.palette.primary.main,
	transition: 'opacity 0.2s ease-in-out',
	...theme.mixins.absoluteFill,
}));

const CategoryCardTitleBox = styled('div', {
	name: CATEGORY_CARD_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
	height: '15%',
	width: '100%',
	textDecoration: 'none',
	color: theme.palette.text.primary,
}));

const SvgIcon = styled(FontAwesomeIcon, {
	name: CATEGORY_CARD_NAME,
	slot: 'Root',
})(({theme}) => ({
	color: 'lightgrey',
	height: '100% !important',
	width: '100% !important',
}));

export interface CategoryCardProps {
	category?: ICategory;
	classes?: CategoryCardClasses;
	className?: string;
	displayDescription?: boolean;
}

export const CategoryCard = ({
	category,
	classes: _classes,
	className,
	displayDescription = true,
}: CategoryCardProps) => {
	const hasDescription = displayDescription && Boolean(category?.description);

	const assetLikeObject =
		category && category.assetId
			? {
					assetId: category.assetId,
					jwt: category.jwt,
					previewUrl: category.previewUrl,
			  }
			: undefined;

	const imageUrls = useAssetImage(assetLikeObject);

	const hasImage = Boolean(imageUrls.preview);

	const ownerState: CategoryCardOwnerState = {
		classes: _classes,
		className,
		hasDescription,
		hasImage,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<CategoryCardRoot
			className={clsx(classes.root, className)}
			href={category ? `#category/${category.categoryid}` : undefined}
			ownerState={ownerState}
		>
			{!category ? (
				<>
					<Skeleton variant="rectangular" width="100%" height="85%" />
					<Skeleton variant="text" width="33%" height="15%" />
				</>
			) : (
				<>
					<CategoryCardActionBox className={classes.actionBox}>
						{imageUrls.preview ? (
							<CategoryImageContainer
								src={imageUrls.preview}
								alt={category.name}
								className={classes.imageContainer}
							/>
						) : (
							<SvgIcon
								className={classes.svgIcon}
								icon={faFolder}
								data-testid="svg"
							/>
						)}
						{hasDescription && (
							<CategoryDescription
								className={classes.description}
								variant="body1"
							>
								{newlineStringToElements(category.description)}
							</CategoryDescription>
						)}
					</CategoryCardActionBox>
					<CategoryCardTitleBox className={classes.titleBox}>
						<Typography variant="body1" noWrap>
							{category.name}
						</Typography>
					</CategoryCardTitleBox>
				</>
			)}
		</CategoryCardRoot>
	);
};
export default CategoryCard;
